import { Section } from "../../components/Section/Section";
import { Content } from "../../components/Content/Content";
import Media from "../../components/Media/Media";
import { useRef } from "react";
import styles from "./MediaBackground.module.scss";
import { useSublinesVisibility } from "../../hooks/useSublinesOverlinesVisibility";
import { AnimatedContent } from "../../components/Animation/Animation";
import { Modul_Text_mit_Bildhintergrund } from "@/wp/types/acf-module-types";
import { Options_Modul_Text_mit_Bildhintergrund } from "@/wp/types/acf-module-options-types";
import { columnSize } from "@/utils/layout/columnSize";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import useWindowSize from "@/hooks/useWindowSize";
import { BreakpointsOptionsType } from "@/getData/getBreakpoints";

export type MediaBackgroundModuleType = Modul_Text_mit_Bildhintergrund;

interface MediaBackgroundProps {
  data: MediaBackgroundModuleType;
  mediaBackgroundOptions: Options_Modul_Text_mit_Bildhintergrund;
  breakpoints: BreakpointsOptionsType | null;
}

const MediaBackground = ({
  data,
  mediaBackgroundOptions,
  // breakpoints,
}: MediaBackgroundProps) => {
  const {
    id,
    section_margin_class,
    textposition_horizontal,
    textposition_vertical,
    module_width,
    row_direction_class_mobile,
    background_color_class,
  } = data.layout;

  const {
    button_type,
    show_extended_link,
    show_overline_subline,
    type,
    height,
    min_height,
    media_format,
    columns,
    columns_text,
    box_opacity,
    gradient,
    overlay_background_color,
    text_color,
  } = mediaBackgroundOptions;

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline,
  );

  const { width } = useWindowSize();
  const isDesktop = width >= 1024;

  const mediaRef = useRef<HTMLDivElement>(null);

  {
    /* jscpd:ignore-start */
  }
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }

  const generateMinHeightStyles = (minHeight: typeof min_height) => {
    const styles: Record<string, string> = {};
    if (minHeight.mobile)
      styles["--min-height-mobile"] = `${minHeight.mobile}px`;
    if (minHeight.tablet)
      styles["--min-height-tablet"] = `${minHeight.tablet}px`;
    if (minHeight.desktop)
      styles["--min-height-desktop"] = `${minHeight.desktop}px`;
    if (minHeight.fullhd)
      styles["--min-height-fullhd"] = `${minHeight.fullhd}px`;
    else if (minHeight.desktop)
      styles["--min-height-fullhd"] = `${minHeight.desktop}px`;

    return styles;
  };

  const minHeightStyles = generateMinHeightStyles(min_height);
  const {
    headline,
    headline_html_tag,
    link,
    content,
    overline,
    subline,
    link_extended,
  } = data.mediabackground ?? {};

  let row_direction_class = "";

  switch (textposition_horizontal) {
    case "left":
      row_direction_class = "is-row-normal-desktop";
      break;
    case "right":
      row_direction_class = "is-row-reverse-desktop";
      break;
    case "centered":
      row_direction_class = "is-row-normal-desktop is-justify-content-center";
      break;
    default:
      row_direction_class = "is-row-normal-desktop";
      break;
  }

  const textMediaJSX = (
    <>
      {/* jscpd:ignore-start */}
      {/* //* Text Column */}
      <div
        className={`column text-col ${styles["text-col"]} ${row_direction_class_mobile === "is-column-reverse-mobile" ? "is-12-mobile" : ""} ${styles[textposition_horizontal]} ${height === "content-height" ? styles["middle"] : styles[textposition_vertical]}
        ${row_direction_class}
        ${columnSize(columns_text)}
        `}
      >
        <div
          className={styles["text-overlay"]}
          style={{
            background:
              type === "overlay-box" || !isDesktop
                ? `var(--${overlay_background_color}) `
                : "transparent",
            opacity:
              isDesktop || row_direction_class_mobile === "is-overlay"
                ? box_opacity
                : 1,
          }}
        ></div>
        <div
          className={`is-fractional-container is-1-2-container ${isDesktop && background_color_class ? background_color_class || "" : ""} ${styles["content-wrapper"]} ${type === "overlay-box" ? styles["overlay-box"] : ""} `}
          style={
            isDesktop
              ? ({
                  background: "transparent",
                  "--text-color": `var(--${text_color})`,
                } as React.CSSProperties)
              : {}
          }
        >
          <AnimatedContent direction={row_direction_class!}>
            <Content
              headline_text_align_class={"has-text-left"}
              text_align_class={"has-text-left"}
              headline_html_tag={headline_html_tag}
              headline={headline}
              text={content ?? ""}
              link={link}
              button_type={button_type}
              overline={showOverline ? overline : ""}
              subline={showSubline ? subline : ""}
              link_extended={show_extended_link ? link_extended : undefined}
            />
          </AnimatedContent>
        </div>
      </div>
      {/* jscpd:ignore-end */}

      {/* <div
        className={`column spacer-col is-hidden-touch is-hidden-desktop-only ${offsetColumns}`}
      /> */}
      {/* //* Meida Column */}
      <div
        ref={mediaRef}
        className={`column ${styles["media-col"]} media-col is-12 is-align-self-stretch`}
      >
        {data.mediabackground.media && (
          <Media
            mediaData={data.mediabackground.media}
            mediaFormat={media_format}
            showAudioIcon={false}
            sizes="100vw"
          />
        )}
        {((type === "full-overlay" && isDesktop) ||
          (row_direction_class_mobile === "is-overlay" && !isDesktop)) && (
          <div
            className={`${styles.overlay}`}
            style={{
              background: gradient ? `${gradient}` : "transparent",
            }}
          ></div>
        )}
      </div>
    </>
  );

  // changes from https://trello.com/c/IUKbIjsh
  let section_margin = "";
  let container_padding = "";
  switch (section_margin_class) {
    case "is-gapless-margin-bottom":
      section_margin = "mb-0";
      container_padding = "pb-0";
      break;
    case "is-gapless-margin-top":
      section_margin = "mt-0";
      container_padding = "pt-0";
      break;
    case "is-gapless-margin-top-bottom":
      section_margin = "my-0";
      container_padding = "py-0";
      break;
    case "margin-top-bottom":
      section_margin = "margin-top-bottom";
      container_padding = "";
      break;
  }

  return (
    <Section
      data-direction={row_direction_class}
      className={`media-background is-clipped is-flex ${module_width} ${styles["media-background"]} ${section_margin} ${styles[height]} py-0`}
      disableColumns
      disableContainer={module_width === "frameless"}
      frameless={module_width === "frameless"}
      id={id}
      // apply min-height styles only when height = "content-height"
      style={height === "content-height" ? { ...minHeightStyles } : {}}
      data-text-overlay={row_direction_class_mobile === "is-overlay"}
      containerClassName={container_padding}
    >
      {module_width === "narrow-frame" ? (
        <div
          className={`columns is-centered ${styles["out-columns"]} m-0 ${row_direction_class === "is-row-reverse-desktop" ? row_direction_class : ""}`}
        >
          <div className={`column ${columnSize(columns)} p-0`}>
            <div
              className={`columns is-gapless is-multiline ${row_direction_class_mobile === "is-column-reverse-mobile" ? `is-mobile ${row_direction_class_mobile} is-column-reverse-tablet` : ""} ${styles["inner-columns"]} ${row_direction_class}`}
            >
              {textMediaJSX}
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`columns  ${styles.columns} 
          ${row_direction_class_mobile === "is-column-reverse-mobile" ? `is-mobile ${row_direction_class_mobile} is-column-reverse-tablet` : ""}
          ${row_direction_class_mobile === "is-column-normal-mobile" ? `is-mobile ${row_direction_class_mobile} is-column-normal-tablet` : ""}
          ${styles[module_width]} ${styles[textposition_vertical]} is-gapless is-multiline ${row_direction_class}`}
        >
          {textMediaJSX}
        </div>
      )}
    </Section>
  );
};

export default MediaBackground;
